import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowLeft,
    Download,
    User,
    Briefcase,
    Calendar,
    ChevronDown,
    ChevronUp,
    TrendingUp,
    TrendingDown,
    DollarSign,
    CheckCircle2,
    Clock,
    AlertCircle,
    FileText,
    History,
    Mail,
    Minus,
    Check,
    PieChart,
    ChevronRight,
    MapPin,
    CreditCard,
    ShieldCheck
} from 'lucide-react';
import { payslipsApi } from '../../../api/payslipsApi';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import { generatePayslipPDF } from '../../../utils/generatePayslipPDF';

const PayslipDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isStructureOpen, setIsStructureOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('Overview');

    const { data: payslipData, isLoading, error } = useQuery({
        queryKey: ['payslip', id],
        queryFn: () => payslipsApi.getPayslipById(id),
        select: (res) => res.data || res
    });

    if (isLoading) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium">Loading...</p>
        </div>
    );

    if (error) return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-2xl mx-auto my-8">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-red-900 mb-1">Failed to load payslip</h2>
            <p className="text-sm text-red-600 mb-4">{error.message || 'The requested payslip could not be found.'}</p>
            <Button onClick={() => navigate(-1)} variant="secondary" size="sm">
                Go Back
            </Button>
        </div>
    );

    const payslip = payslipData || {};

    const summary = {
        netPay: payslip.netPay || 0,
        paidPreviously: payslip.paidPreviously || 0,
        paidNow: payslip.paidAmount || payslip.finalPay || 0,
        hold: payslip.holdAmount || 0,
        adjustment: payslip.adjustment || 0,
        totalPaid: (payslip.paidAmount || 0) + (payslip.paidPreviously || 0),
        grossPay: payslip.grossPay || 0
    };

    const attendanceRate = payslip.workingDays > 0
        ? ((payslip.presentDays || 0) / payslip.workingDays * 100).toFixed(1)
        : "0.0";

    const coveragePercent = summary.netPay > 0
        ? Math.min(100, (summary.totalPaid / summary.netPay) * 100).toFixed(1)
        : "0.0";

    const handleExportPDF = async () => {
        try {
            const doc = await generatePayslipPDF(payslip);
            doc.save(`payslip-${payslip.employeeName?.replace(/\s+/g, '_') || 'employee'}-${payslip.payrollMonth || 'period'}.pdf`);
        } catch (error) {
            console.error("Export PDF failed:", error);
        }
    };

    return (
        <div className="max-w-6xl mx-3 py-2 space-y-6 bg-[#fafafa] min-h-screen">

            {/* HEADER */}
            <div className="flex items-center justify-between">
                <Button
                    onClick={() => navigate(-1)}
                    variant="outline"
                    size="sm"
                    icon={<ArrowLeft className="h-4 w-4" />}
                >
                    Payslips
                </Button>
                <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-100 text-green-700 rounded text-[11px] font-bold uppercase tracking-wider">
                        <Check className="h-3 w-3" /> {payslip.status || 'Generated'}
                    </span>
                    <Button
                        onClick={handleExportPDF}
                        variant="outline"
                        size="sm"
                        icon={<Download className="h-4 w-4" />}
                    >
                        Export PDF
                    </Button>
                </div>
            </div>

            {/* PROFILE */}
            <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-[#0F2942] text-lg font-bold border-2 border-white shadow-sm">
                    {payslip.employeeName?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">{payslip.employeeName || 'N/A'}</h1>
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                        <span>#{payslip.employeeId || 'N/A'}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span className="text-gray-700 font-semibold">{payslip.designation || payslip.department || 'N/A'}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span>{payslip.email || 'N/A'}</span>
                    </div>
                </div>
            </div>

            {/* CONTENT */}
            <div className="space-y-4">

                {/* 1. SUMMARY CARD */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col md:flex-row">
                    <div className="md:w-1/2 p-6 md:border-r border-gray-100 flex flex-col justify-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">NET PAY — {payslip.payrollMonth || 'N/A'}</p>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">₹{summary.netPay.toLocaleString('en-IN')}</h2>
                        <p className="text-[11px] text-gray-500 font-medium">
                            Pay period: <span className="text-gray-900 font-bold">{payslip.payrollMonth || 'N/A'}</span> <span className="mx-1.5 text-gray-300">•</span>
                            Gross: <span className="text-gray-900 font-bold">₹{summary.grossPay.toLocaleString('en-IN')}</span> <span className="mx-1.5 text-gray-300">•</span>
                            Mode: <span className="text-gray-900 font-bold">{payslip.mode || 'N/A'} {payslip.input ? `${payslip.input}${payslip.mode === 'Percentage' ? '%' : ''}` : ''}</span>
                        </p>
                    </div>
                    <div className="md:w-1/2 bg-gray-50/20 p-6 grid grid-cols-2 gap-3">
                        <div className="bg-white p-3.5 rounded-lg border border-gray-100 shadow-sm">
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">PAID NOW</p>
                            <p className="text-lg font-bold text-gray-900">₹{summary.paidNow.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-lg border border-gray-100 shadow-sm">
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">PREV. PAID</p>
                            <p className="text-lg font-bold text-gray-900">₹{summary.paidPreviously.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-lg border border-gray-100 shadow-sm">
                            <p className="text-[9px] font-bold text-orange-400 uppercase tracking-wider mb-1">ON HOLD</p>
                            <p className="text-lg font-bold text-gray-900">₹{summary.hold.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-lg border border-gray-100 shadow-sm">
                            <p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider mb-1">ADJUSTMENT</p>
                            <p className="text-lg font-bold text-[#0F2942]">{summary.adjustment >= 0 ? '+' : ''}₹{summary.adjustment.toLocaleString('en-IN')}</p>
                        </div>
                    </div>
                </div>

                {/* 2. DETAILS & ATTENDANCE */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-bold text-gray-900">Employee details</h3>
                        </div>
                        <div className="p-4 space-y-3">
                            {[
                                { label: 'Employee ID', value: payslip.employeeId || 'N/A' },
                                { label: 'Department', value: payslip.department || 'N/A' },
                                { label: 'Designation', value: payslip.designation || 'N/A' },
                                { label: 'Join Date', value: payslip.joinDate ? formatDate(payslip.joinDate, { includeTime: false }) : 'N/A' },
                                { label: 'Working days', value: payslip.workingDays || 0 },
                                { label: 'Present days', value: payslip.presentDays || 0 }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                                    <span className="text-xs font-bold text-gray-900 text-right">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-bold text-gray-900">Bank & Compliance</h3>
                        </div>
                        <div className="p-4 space-y-3">
                            {[
                                { label: 'Bank Name', value: payslip.bankName || 'N/A' },
                                { label: 'Account No', value: payslip.bankAccount || 'N/A' },
                                { label: 'IFSC Code', value: payslip.ifsc || 'N/A' },
                                { label: 'PAN', value: payslip.pan || 'N/A' },
                                { label: 'UAN', value: payslip.uan || 'N/A' },
                                { label: 'ESI No', value: payslip.esi || 'N/A' }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                                    <span className="text-xs font-bold text-gray-900 text-right">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <h3 className="text-sm font-bold text-gray-900">Attendance</h3>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{payslip.payrollMonth || 'N/A'}</span>
                        </div>
                        <div className="p-4 space-y-6">
                            <div className="flex items-end justify-between">
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">RATE</p>
                                    <h4 className="text-2xl font-bold text-gray-900">{attendanceRate}%</h4>
                                </div>
                                <div className="text-right">
                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Present / Working</p>
                                    <p className="text-base font-bold text-gray-900">
                                        {payslip.presentDays || 0} / <span className="text-gray-500">{payslip.workingDays || 0}</span>
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex gap-1 h-6 items-end">
                                    {[...Array(Math.min(payslip.workingDays || 0, 31))].map((_, i) => {
                                        const isPresent = i < (payslip.presentDays || 0);
                                        return (
                                            <div
                                                key={i}
                                                className={`flex-1 rounded-t-sm ${isPresent ? 'bg-green-400 h-full' : 'bg-red-400 h-1/2'}`}
                                            ></div>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center gap-3 text-[9px] font-bold uppercase">
                                    <div className="flex items-center gap-1 text-green-600">
                                        <div className="w-2 h-2 bg-green-400 rounded-sm"></div> Present ({payslip.presentDays || 0})
                                    </div>
                                    <div className="flex items-center gap-1 text-red-600">
                                        <div className="w-2 h-2 bg-red-400 rounded-sm"></div> Absent ({(payslip.workingDays - payslip.presentDays) || 0})
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. SALARY STRUCTURE */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-bold text-gray-900">Salary structure</h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="px-2 py-0.5 bg-blue-50 text-[#0B2033] text-[10px] font-bold uppercase rounded-md border border-blue-100">
                                CTC ₹{(payslip.salaryStructure?.ctc || 0).toLocaleString('en-IN')}
                            </span>
                            <button onClick={() => setIsStructureOpen(!isStructureOpen)}>
                                {isStructureOpen ? <Minus className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                            </button>
                        </div>
                    </div>
                    {isStructureOpen && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-4 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider">COMPONENT</th>
                                        <th className="px-4 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider">TYPE</th>
                                        <th className="px-4 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider text-center">%</th>
                                        <th className="px-4 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider text-right">VALUE</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {payslip.salaryStructure?.components?.map((comp, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/30 transition-colors">
                                            <td className="px-4 py-2.5 text-xs font-bold text-gray-900">{comp.name}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${comp.type === 'earning' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                                                    }`}>
                                                    {comp.type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-medium text-gray-500 text-center">
                                                {comp.percentageValue ? `${comp.percentageValue}%` : '—'}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-bold text-right text-gray-900">
                                                {comp.type === 'deduction' ? '-' : ''}₹{(comp.value || 0).toLocaleString('en-IN')}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-blue-50/20 font-bold border-t border-blue-100">
                                        <td colSpan="3" className="px-4 py-3 text-[11px] text-gray-900 uppercase">Annual net take-home</td>
                                        <td className="px-4 py-3 text-sm text-[#0B2033] text-right">₹{(payslip.salaryStructure?.netPay || 0).toLocaleString('en-IN')}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* 4. BREAKDOWN & HISTORY */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-8">
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
                            <History className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-bold text-gray-900">Payment breakdown</h3>
                        </div>
                        <div className="p-4 space-y-0.5 flex-grow">
                            {[
                                { label: 'Gross pay (Annual)', value: summary.grossPay, color: 'bg-gray-400' },
                                { label: 'Net pay (Monthly)', value: summary.netPay, color: 'bg-[#0F2942]', isHL: true },
                                { label: 'Paid previously', value: summary.paidPreviously, color: 'bg-gray-300' },
                                { label: 'Paid this batch', value: summary.paidNow, color: 'bg-green-600' },
                                { label: 'On hold', value: summary.hold, color: 'bg-orange-500' },
                                { label: 'Adjustment', value: summary.adjustment, color: 'bg-blue-400' }
                            ].map((item, idx) => (
                                <div key={idx} className={`flex items-center justify-between py-1.5 ${item.isHL ? 'bg-blue-50/30 px-2 -mx-2 rounded' : ''}`}>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-0.5 ${item.color} rounded-full`}></div>
                                        <span className={`text-xs ${item.isHL ? 'font-bold text-gray-900' : 'font-medium text-gray-500'}`}>{item.label}</span>
                                    </div>
                                    <span className={`text-xs font-bold ${item.isNegative ? 'text-red-600' : item.label === 'Adjustment' ? 'text-[#0F2942]' : 'text-gray-900'}`}>
                                        {item.isNegative ? '-' : item.label === 'Adjustment' && item.value >= 0 ? '+' : ''}₹{Math.abs(item.value).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-50">
                            <div className="flex justify-between text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                <span>Coverage of net pay</span>
                                <span className="text-gray-900">{coveragePercent}%</span>
                            </div>
                            <div className="h-1 w-full bg-gray-200 rounded-full">
                                <div className="h-full bg-green-700 rounded-full" style={{ width: `${coveragePercent}%` }}></div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <h3 className="text-sm font-bold text-gray-900">Payment history</h3>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Month</span>
                        </div>
                        <div className="p-4 space-y-5 flex-grow">
                            {payslip.paymentHistory?.length > 0 ? (
                                <div className="space-y-5 relative before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-[1px] before:bg-gray-100">
                                    {payslip.paymentHistory.map((history, idx) => (
                                        <div key={idx} className="relative pl-5">
                                            <div className={`absolute left-0 top-1 w-4 h-4 rounded-full border bg-white z-10 flex items-center justify-center ${history.isCurrent ? 'border-gray-900' : 'border-gray-200'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${history.isCurrent ? 'bg-gray-900' : 'bg-gray-200'}`}></div>
                                            </div>
                                            <div className="flex justify-between items-start">
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-1.5">
                                                        <h4 className="text-xs font-bold text-gray-900">{history.batchName || 'Disbursement'}</h4>
                                                        {history.isCurrent && <span className="px-1 py-0.2 bg-gray-100 text-gray-600 text-[8px] font-bold uppercase rounded">CURRENT</span>}
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 font-bold">{formatDate(history.paidDate, { includeTime: false })} • {history.input}{history.mode === 'Percentage' ? '%' : ''} {history.mode}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs font-bold text-gray-900">₹{history.paidAmount.toLocaleString('en-IN')}</p>
                                                    <span className="text-[8px] font-bold text-green-600 uppercase">COMPLETED</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-6 text-center">
                                    <p className="text-xs text-gray-400">No previous records.</p>
                                </div>
                            )}
                        </div>
                        <div className="px-4 py-4 border-t border-gray-100 bg-gray-50/20">
                            <div className="flex items-center justify-between">
                                <p className="text-[9px] font-bold text-gray-400 uppercase">Total disbursed</p>
                                <p className="text-base font-bold text-gray-900">₹{payslip.paymentHistory?.reduce((sum, h) => sum + h.paidAmount, 0).toLocaleString('en-IN') || summary.paidNow.toLocaleString('en-IN')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayslipDetailsPage;
